<script setup>
import { computed } from 'vue';
// eslint-disable-next-line
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    size: {
        type: String,
        default: 'md',
        validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].includes(value)
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true
    },
    showHeader: {
        type: Boolean,
        default: true
    },
    bgVariant: {
        type: String,
        default: 'gray',
        validator: (value) => ['gray', 'dark', 'darker'].includes(value)
    },
    showFooter: {
        type: Boolean,
        default: false
    },
    confirmButtonText: {
        type: String,
        default: 'Confirm'
    },
    cancelButtonText: {
        type: String,
        default: 'Cancel'
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    loadingText: {
        type: String,
        default: 'Loading...'
    }
})

const modalSize = computed(() => {
    return {
        'sm': 'w-full max-w-sm',
        'md': 'w-full max-w-md',
        'lg': 'w-full max-w-lg',
        'xl': 'w-full max-w-xl',
        '2xl': 'w-full max-w-2xl',
        '3xl': 'w-full max-w-3xl',
        '4xl': 'w-full max-w-4xl',
        '5xl': 'w-full max-w-5xl',
        '6xl': 'w-full max-w-6xl',
        '7xl': 'w-full max-w-7xl',
    }[props.size]
})

const modalBgColor = computed(() => {
    return {
        'gray': 'bg-echoes-background-gray',
        'dark': 'bg-[#0A0A0A]',
        'darker': 'bg-echoes-background-black',
    }[props.bgVariant]
})

const modalBorderColor = computed(() => {
    return {
        'gray': 'border-echoes-border-light',
        'dark': 'border-[#1A1A1A]',
        'darker': 'border-[#0F0F0F]',
    }[props.bgVariant]
})
// eslint-disable-next-line
const emit = defineEmits({
    close: () => true,
    confirm: () => true,
    cancel: () => true
})

const handleBackdropClick = () => {
    if (props.closeOnClickOutside) {
        emit('close')
    }
}

const handleConfirm = () => {
    emit('confirm')
}

const handleCancel = () => {
    emit('cancel')
    emit('close')
}
</script>
<template>
    <Teleport to="body">
        <div 
            v-if="isOpen"
            class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center"
            @click.self="handleBackdropClick"
        >
            <div 
                :class="[modalBgColor, modalBorderColor, 'border rounded-xl w-full max-h-[90vh] overflow-hidden flex flex-col', modalSize]"
            >
                <div v-if="showHeader" class="relative flex-shrink-0 p-5 pb-4">
                    <div class="flex items-center" :class="title ? 'justify-between' : 'justify-end'">
                        <h1 id="modal-title" class="text-base 2xl:text-lg font-medium">{{ title }}</h1>
                        <button @click="$emit('close')" class="text-lg h-8 w-8 flex items-center justify-center text-white hover:text-red-600 hover:bg-echoes-border-light border border-echoes-border-light hover:none transition-all duration-200 rounded-lg p-2">
                            <i class="uil uil-times"></i>
                        </button>
                    </div>
                    <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
                </div>
                <div class="flex-1 overflow-y-auto px-5" :class="[showHeader ? 'pt-4' : 'pt-5', showFooter ? 'pb-4' : 'pb-5']">
                    <slot />
                </div>
                <div v-if="showFooter" class="flex-shrink-0 relative pt-4 pb-5 px-5">
                    <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
                    <div class="flex justify-end gap-3">
                        <slot name="cancelButton">
                            <button 
                                @click="handleCancel"
                                class="bg-echoes-background-black text-sm py-2 px-4 hover:bg-echoes-background-black/80 text-white font-medium rounded-lg transition-colors duration-200 border border-echoes-border-light"
                            >
                                {{ cancelButtonText }}
                            </button>
                        </slot>
                        <slot name="confirmButton">
                            <button 
                                @click="handleConfirm"
                                :disabled="isLoading"
                                class="bg-echoes-primary text-sm py-2 px-4 hover:bg-echoes-primary/80 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <div v-if="isLoading" class="w-4 h-4 border-2 border-white border-t-echoes-primary rounded-full animate-spin"></div>
                                <span>{{ isLoading ? loadingText : confirmButtonText }}</span>
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template> 